<template>
  <div class="app">
    <portal-target
      name="popup"
      :multiple="true"
    />

    <modal
      name="scan-modal"
      :adaptive="true"
      :height="'auto'"
      width="95%"
    >
      <div
        v-if="!error && !bluetoothError"
        class="container"
      >
        <!-- <div class="modal-content error">{{ error }}</div> -->
        <div class="modal-footer">
          <button-input
            class="button"
            @click.native="onStartScanAndSync"
          >
            {{ modal.msg }}
          </button-input>

          <button-input
            class="button"
            :primary="false"
            @click.native="onSkipClick"
          >
            {{ modal.options[2] }}
          </button-input>
        </div>
      </div>

      <div
        v-if="error"
        class="container"
      >
        <div class="modal-content">
          {{ modal.options[4] }}
        </div>

        <div class="modal-footer">
          <button-input
            class="button"
            :primary="false"
            @click.native="onSkipClick"
          >
            {{ modal.options[3] }}
          </button-input>
        </div>
      </div>

      <div
        v-if="bluetoothError"
        class="container"
      >
        <div class="modal-content">
          {{ bluetoothError }}
        </div>

        <div class="modal-footer">
          <button-input
            class="button"
            @click.native="onStartScanAndSync"
          >
            {{ modal.options[6] }}
          </button-input>

          <button-input
            class="button"
            :primary="false"
            @click.native="onSkipClick"
          >
            {{ modal.options[2] }}
          </button-input>
        </div>
      </div>
    </modal>

    <Layout v-if="sensorTypes && !sync">
      <template #content>
        <keep-alive>
          <router-view />
        </keep-alive>
      </template>
    </Layout>

    <LoadingScreen v-if="sync" />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import Layout from '@/components/AppLayout';
import LoadingScreen from '@/components/LoadingScreen';
import store from '@/store';
import PublicPackageApi from '@/api/public';
import * as MoecoSdkLib from '@/js/packages/moecoSdk-sdk/kotlin/moecoSdk-sdk.js';
// import browser from "browser-detect";

const waitInSeconds = (seconds) => {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(async () => {
      resolve();
      clearTimeout(timeoutId);
    }, seconds * 1000);
  });
};

const prepareHash = (hash) => {
  let output = [];
  for (let i = 0; i < hash.length; i += 2) {
    if (i == 0) {
      output[0] = hash[0] + hash[1];
    } else {
      output[i / 2] = hash[i] + hash[i + 1];
    }
  }
  return output.join(':');
};

import {
  loggerImpl,
  getBleScannerImpl,
  getLocationProviderImpl,
} from '@/libs/ble';

export default {
  name: 'App',
  components: {
    Layout,
    LoadingScreen,
  },

  data() {
    const modalMsgOptions = [
      'Upload the latest update from the sensor',
      'Try again',
      'Show the last avalaible data',
      'Show the last avalaible data',
      "Oops, something went wrong, or your current browser doesn't support the data update. We recommend to use Google Chrome.",
      'Error while getting data.',
      'Try again',
    ];
    return {
      sync: false,
      error: null,
      bluetoothError: null,
      modal: {
        msg: modalMsgOptions[0],
        options: modalMsgOptions,
      },
    };
  },
  computed: {
    ...mapGetters({
      track: 'packageDetails/track/list',
      sensorUid: 'shipments/sensorUid',
      errors: 'errors/errors',
      altErrors: 'errors/altErrors',
      blockErrors: 'errors/block',
      sensorTypes: 'sensorTypes/list',
      location: 'location/location',
    }),
  },

  watch: {
    errors: {
      deep: true,
      handler: function () {
        if (!this.blockErrors) {
          this.setBlockErrors(true);
          setTimeout(() => {
            this.setBlockErrors(false);
            const errors = Object.entries(this.altErrors);
            for (let e of errors) {
              for (let errorItem of e[1].errors) {
                let errorItemMessage = `${e[0]}: ${errorItem}`;
                this.$toast.error(errorItemMessage, {
                  position: 'top-left',
                  duration: 6000,
                });
              }

              const errorKey = e[0];
              this.popError(errorKey);
            }
          }, 1000);
        }
      },
    },
  },

  mounted() {
    // this.waitNewTransactions();

    this.$modal.show('scan-modal');

    this.setLayoutTitle(`Sensor ${prepareHash(this.sensorUid)}`);
    this.getSensorTypes();

    this.changeTrackParams({
      distance: 512000,
      hash: this.sensorUid,
    });
    this.getTrack();
  },

  methods: {
    ...mapActions({
      popError: 'errors/popError',
      setBlockErrors: 'errors/setBlockErrors',
      getSensorTypes: 'sensorTypes/getList',
      changeTrackParams: 'packageDetails/track/changeParams',
      getTrack: 'packageDetails/track/getList',
    }),
    ...mapMutations({
      setLayoutTitle: 'layout/setLayoutTitle',
    }),

    async getLatestTransaction() {
      const { data } = await PublicPackageApi.getTransactions({
        hash: this.sensorUid,
        limit: {
          count: 1,
          offset: 0,
        },
        orderBy: {
          value: 'timestamp',
          desc: true,
        },
      });

      return data[0];
    },

    async waitNewTransactions() {
      const latestTx = await this.getLatestTransaction();
      const prevId = latestTx._id;

      let stop = false;
      this.error = null;

      const DEFAULT_DATA_SYNC_TIMEOUT = 120000;

      const timeout_ = process.env.VUE_APP_NO_DATA_RECEIVED_TIMEOUT
        ? process.env.VUE_APP_NO_DATA_RECEIVED_TIMEOUT
        : DEFAULT_DATA_SYNC_TIMEOUT;

      const timeoutId = setTimeout(() => {
        stop = true;
        this.bluetoothError = 'No data received.';
        this.$modal.show('scan-modal');
        this.sync = false;
      }, timeout_);

      let newId = prevId;
      while (newId === prevId || stop === true) {
        const txToCheck = await this.getLatestTransaction();
        newId = txToCheck._id;
        await waitInSeconds(1);
      }

      clearTimeout(timeoutId);
    },

    async onStartScanAndSync() {
      this.error = null;
      this.bluetoothError = null;

      let bleScannerImpl;
      const locationProviderImpl = getLocationProviderImpl(store);
      try {
        bleScannerImpl = await getBleScannerImpl(store, () => {
          this.sync = true;
        });
      } catch (error) {
        this.sync = false;
        this.bluetoothError = this.modal.options[5];
        return;
      }

      const moecoSdk = new MoecoSdkLib.io.moeco.sdk.di.MoecoSdk(
        bleScannerImpl,
        locationProviderImpl,
        [loggerImpl]
      );

      const gateToken = process.env.VUE_APP_GATE_TOKEN;

      const DEFAULT_WEBSOCKET_PORT = 9900;

      const port = process.env.VUE_APP_GATE_WEBSOCKET_PORT
        ? process.env.VUE_APP_GATE_WEBSOCKET_PORT
        : DEFAULT_WEBSOCKET_PORT;

      const ws = { address: process.env.VUE_APP_GATE_WEBSOCKET, port };

      this.$modal.hide('scan-modal');
      moecoSdk.startDeviceSynchronization(
        null,
        gateToken,
        ws,
        async () => {
          console.log('onsucess');
          await this.waitNewTransactions();
          this.sync = false;
        },
        (err) => {
          console.log('error', err);
          this.$modal.show('scan-modal');
          this.sync = false;
          this.bluetoothError = this.modal.options[5];
          return;
        }
      );
    },

    onSkipClick() {
      this.$modal.hide('scan-modal');
    },
  },
};
</script>



<style lang="scss" scoped>
.app {
  height: 100%;
  width: 100%;
}
.container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 16px;

  color: $notBlack;

  .error {
    color: $accentRed;
  }

  .modal-header {
    @include h1;
  }

  .modal-content {
    flex: 1;
  }

  .modal-footer {
    width: 100%;
    .button {
      height: 60px;
      margin-top: 5px;
      width: inherit;
    }
  }
}
</style>
